import React, { useState, useEffect } from 'react';
import { Megaphone, Upload, Save, Trash2, ExternalLink, Image, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const QUIZ_OPTIONS = [
  { id: 'general-safety', name: 'Truck Safety Knowledge Test' },
  { id: 'accident-prevention', name: 'Accident Prevention Quiz' },
  { id: 'load-securement', name: 'Load Securement Safety Quiz' }
];

const QuizSponsorManager = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState('general-safety');
  const [formData, setFormData] = useState({
    sponsor_name: '',
    sponsor_message: '',
    banner_url: '',
    click_url: '',
    active: true
  });
  const [uploading, setUploading] = useState(false);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchSponsors();
  }, []);

  useEffect(() => {
    // Load existing sponsor data when quiz selection changes
    const existing = sponsors.find(s => s.quiz_id === selectedQuiz);
    if (existing) {
      setFormData({
        sponsor_name: existing.sponsor_name || '',
        sponsor_message: existing.sponsor_message || '',
        banner_url: existing.banner_url || '',
        click_url: existing.click_url || '',
        active: existing.active !== false
      });
    } else {
      setFormData({
        sponsor_name: '',
        sponsor_message: '',
        banner_url: '',
        click_url: '',
        active: true
      });
    }
  }, [selectedQuiz, sponsors]);

  const fetchSponsors = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/api/admin/quiz-sponsors/list`, { headers });
      setSponsors(response.data.sponsors || []);
    } catch (error) {
      console.error('Failed to fetch sponsors:', error);
      toast.error('Failed to load sponsors');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.sponsor_name) {
      toast.error('Please enter a sponsor name');
      return;
    }

    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/api/admin/quiz-sponsors/create`, {
        quiz_id: selectedQuiz,
        ...formData
      }, { headers });
      
      toast.success('Sponsor saved successfully');
      fetchSponsors();
    } catch (error) {
      console.error('Failed to save sponsor:', error);
      toast.error('Failed to save sponsor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove this sponsor?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/api/admin/quiz-sponsors/${selectedQuiz}`, { headers });
      toast.success('Sponsor removed');
      setFormData({
        sponsor_name: '',
        sponsor_message: '',
        banner_url: '',
        click_url: '',
        active: true
      });
      fetchSponsors();
    } catch (error) {
      console.error('Failed to delete sponsor:', error);
      toast.error('Failed to remove sponsor');
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API}/api/admin/quiz-sponsors/upload-banner`,
        formDataUpload,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        setFormData(prev => ({ ...prev, banner_url: response.data.banner_url }));
        toast.success('Banner uploaded');
      }
    } catch (error) {
      console.error('Failed to upload banner:', error);
      toast.error('Failed to upload banner');
    } finally {
      setUploading(false);
    }
  };

  const existingSponsor = sponsors.find(s => s.quiz_id === selectedQuiz);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-orange-500" />
            Quiz Sponsors
          </h2>
          <p className="text-gray-400 mt-1">Add sponsor banners and messages to quiz result emails</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Configure Sponsor</CardTitle>
            <CardDescription>Set up a sponsor for a quiz. Their banner and message will appear in result emails.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quiz Selection */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Select Quiz</label>
              <div className="grid grid-cols-1 gap-2">
                {QUIZ_OPTIONS.map((quiz) => {
                  const hasSponsor = sponsors.some(s => s.quiz_id === quiz.id && s.active);
                  return (
                    <button
                      key={quiz.id}
                      onClick={() => setSelectedQuiz(quiz.id)}
                      className={`p-3 rounded-lg text-left transition-all ${
                        selectedQuiz === quiz.id
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{quiz.name}</span>
                        {hasSponsor && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">Sponsored</Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sponsor Name */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Sponsor Name *</label>
              <Input
                value={formData.sponsor_name}
                onChange={(e) => setFormData(prev => ({ ...prev, sponsor_name: e.target.value }))}
                placeholder="e.g., SafeHaul Equipment Co."
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            {/* Sponsor Message */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Sponsor Message</label>
              <Textarea
                value={formData.sponsor_message}
                onChange={(e) => setFormData(prev => ({ ...prev, sponsor_message: e.target.value }))}
                placeholder="e.g., SafeHaul has been providing quality load securement equipment since 1985. Visit us for 15% off your first order!"
                className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
              />
            </div>

            {/* Banner Image */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Banner Image</label>
              <div className="flex gap-2">
                <Input
                  value={formData.banner_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, banner_url: e.target.value }))}
                  placeholder="https://... or upload below"
                  className="bg-gray-800 border-gray-700 text-white flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-gray-700 text-gray-300"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-white" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">Recommended: 560x200px, max 2MB</p>
            </div>

            {/* Click URL */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Click URL (Sponsor Website)</label>
              <Input
                value={formData.click_url}
                onChange={(e) => setFormData(prev => ({ ...prev, click_url: e.target.value }))}
                placeholder="https://sponsor-website.com"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
              <div>
                <p className="text-white font-medium">Active</p>
                <p className="text-gray-500 text-sm">Show sponsor in quiz emails</p>
              </div>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSave}
                disabled={saving || !formData.sponsor_name}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                {saving ? 'Saving...' : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Sponsor
                  </>
                )}
              </Button>
              {existingSponsor && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="border-red-800 text-red-400 hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Email Preview</CardTitle>
            <CardDescription>How the sponsor section will appear in quiz result emails</CardDescription>
          </CardHeader>
          <CardContent>
            {formData.sponsor_name ? (
              <div className="bg-gradient-to-br from-orange-50 to-white p-5 rounded-lg border border-orange-200">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">
                  This quiz is sponsored by
                </p>
                <p className="text-lg font-bold text-gray-900 mb-3">
                  {formData.sponsor_name}
                </p>
                
                {formData.banner_url && (
                  <div className="mb-3">
                    <img
                      src={formData.banner_url.startsWith('/') ? `${API}${formData.banner_url}` : formData.banner_url}
                      alt="Sponsor banner"
                      className="w-full h-auto rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {formData.sponsor_message && (
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                    {formData.sponsor_message}
                  </p>
                )}
                
                {formData.click_url && (
                  <a
                    href={formData.click_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-semibold hover:bg-orange-600 transition-colors"
                  >
                    Learn More <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Image className="h-12 w-12 mb-4 opacity-50" />
                <p>Enter sponsor details to see preview</p>
              </div>
            )}

            {existingSponsor && (
              <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <AlertCircle className="h-4 w-4" />
                  <span>
                    Click count: <strong className="text-white">{existingSponsor.click_count || 0}</strong>
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuizSponsorManager;
